import { useGetCustomerUserCategories } from "controller";
import { useMemo } from "react";
import type { CustomerUserCategory, CustomerUserCategoryTier } from "shared";
import { FALLBACK_CATEGORIES } from "./fallbackCategories";

/**
 * The live endpoint returns no `tier` — only `level` (Standard = 10) — but the
 * crown artwork is chosen per tier. So derive the tier from the row's RANK once
 * the list is sorted by `level` descending: the top-ranked category gets the
 * gradient crown, then gold, then silver, and everything from fourth place down
 * gets the plain outline.
 *
 * Rank, not name, because names are user-editable via
 * PATCH /configuration/user-categories/{id} — renaming "VIP" must not cost it
 * its crown. Rank also degrades sanely if an MSP ever has more or fewer than the
 * four categories seen today (extras simply render the outline crown).
 *
 * Falls back to array order when `level` is absent, since the API returns
 * highest-first.
 */
const TIER_BY_RANK: CustomerUserCategoryTier[] = [
  "customer_owner",
  "vip",
  "manager",
  "standard",
];

const withDerivedTiers = (
  rows: CustomerUserCategory[],
): CustomerUserCategory[] => {
  const ranked = [...rows].sort((a, b) => (b.level ?? 0) - (a.level ?? 0));
  const tierById = new Map<string, CustomerUserCategoryTier>(
    ranked.map((row, index) => [
      row.mapping_id,
      TIER_BY_RANK[index] ?? "standard",
    ]),
  );
  // Preserve the backend's own ordering for display; only attach the tier.
  return rows.map((row) => ({
    ...row,
    tier: row.tier ?? tierById.get(row.mapping_id) ?? "standard",
  }));
};

export const useCustomerUserCategoryOptions = () => {
  const { data } = useGetCustomerUserCategories();

  const options = useMemo<CustomerUserCategory[]>(
    () =>
      withDerivedTiers(
        Array.isArray(data) && data.length > 0 ? data : FALLBACK_CATEGORIES,
      ),
    [data],
  );

  // Preference order, most authoritative first:
  //  1. the row the backend flags `is_default` — but ONLY when exactly one row
  //     claims it. The live endpoint currently returns `is_default: true` on all
  //     four rows (captured 2026-08-27), which makes the flag carry no
  //     information at all; taking the first match there silently reinstates the
  //     "C-level is preselected" bug, since C-level leads the list. Treat an
  //     ambiguous flag as absent rather than as a vote for whoever sorts first.
  //  2. the lowest `level` — the backend's own ordering key, which survives a
  //     rename via PATCH /configuration/user-categories/{id} where a name match
  //     would not. Picks Standard on today's data.
  //  3. the FIRST option, never the last. Falling back to the last row is what
  //     made an arbitrary category ("C-level") win whenever step 1 missed.
  //
  // Step 1 stays ahead of step 2 so that the day the backend flags a single row,
  // its choice wins even if it is not the lowest level.
  const defaultValue = useMemo(() => {
    const flagged = options.filter((option) => option.is_default);
    if (flagged.length === 1) return flagged[0].mapping_id;

    const byLevel = [...options]
      .filter((option) => typeof option.level === "number")
      .sort((a, b) => (a.level ?? 0) - (b.level ?? 0))[0];
    if (byLevel) return byLevel.mapping_id;

    return options[0]?.mapping_id ?? "";
  }, [options]);

  return { options, defaultValue };
};
